import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Sparkles, MessageSquare, Info, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { Card, PageHeader, Button } from "../components/Primitives";
import { listNotifications, resolveNotification, findChatForRequest, updateProposalStatus, createOrGetChat, subscribeNotifications, resolveProposalIdForNotif, markNotificationRead } from "../lib/db";

const ICONS = {
  match: Sparkles,
  proposal: Bell,
  message: MessageSquare,
  system: Info,
};

const ACTIONS_FOR = {
  proposal: ["openProposal"],
  match: ["openRequest", "message"],
  message: ["message"],
  system: [],
};

export default function Notifications() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!user?.id) { setLoading(false); return; }
    const fetchList = () => listNotifications(user.id).then((rows) => { if (alive) setList(rows); });
    fetchList().finally(() => { if (alive) setLoading(false); });
    const unsub = subscribeNotifications(user.id, () => { fetchList(); });
    return () => { alive = false; unsub && unsub(); };
  }, [user?.id]);

  const markOneRead = async (id) => {
    setList((prev) => prev.map((x) => (x.id === id ? { ...x, unread: false } : x)));
    try { await markNotificationRead(id); } catch { /* ignore */ }
  };

  const openRequestOf = (n) => {
    const raw = list.find((x) => x.id === n.id);
    const payload = raw?.payload || {};
    // For proposal notifications, prefer navigating directly to the Proposal Detail
    if ((raw?.type === "proposal") && payload.proposal_id) {
      navigate(`/app/proposals/${payload.proposal_id}`);
      return;
    }
    const ref = payload.request_ref || payload.request_id;
    if (ref) navigate(`/app/requests/${ref}`);
    else navigate("/app/requests");
  };

  const messageOf = async (n) => {
    const raw = list.find((x) => x.id === n.id);
    const payload = raw?.payload || {};
    if (payload.request_id && user?.id) {
      // Prefer the exact applicant-scoped chat if we know the applicant_id
      let chatId = await findChatForRequest(user.id, payload.request_id, payload.applicant_id || null);
      if (!chatId && payload.applicant_id) {
        // create it if missing
        try { chatId = await createOrGetChat(user.id, payload.applicant_id, payload.request_id); } catch { /* ignore */ }
      }
      if (chatId) { navigate(`/app/messages?chat=${chatId}`); return; }
    }
    navigate("/app/messages");
  };

  const gotoProposalOrFallback = async (n) => {
    const raw = list.find((x) => x.id === n.id);
    const payload = raw?.payload || {};
    await markOneRead(n.id);
    // Always try to land on the Proposal Detail for proposal notifications
    let pid = payload.proposal_id;
    if (!pid && user?.id) {
      try { pid = await resolveProposalIdForNotif(user.id, payload); } catch { /* ignore */ }
    }
    if (pid) { navigate(`/app/proposals/${pid}`); return; }
    // Legacy fallback: request detail if we know the request, else the requests list
    const ref = payload.request_ref || payload.request_id;
    if (ref) navigate(`/app/requests/${ref}`);
    else navigate("/app/requests");
  };

  const handleAction = async (n, action) => {
    if (action === "openProposal") { await gotoProposalOrFallback(n); return; }
    await markOneRead(n.id);
    if (action === "message") await messageOf(n);
    else if (action === "openRequest") openRequestOf(n);
  };

  const openNotificationRow = async (n) => {
    // Whole-row navigation for proposal notifications
    if (n.type === "proposal") { await gotoProposalOrFallback(n); }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto animate-fade-up">
      <PageHeader
        title={t("notifications.title")}
        testId="notifications-header"
      />

      {loading ? (
        <div className="text-ink-500 font-mono text-xs py-8 text-center">Loading…</div>
      ) : list.length === 0 ? (
        <Card className="p-10 text-center" data-testid="notif-empty">
          <Bell className="w-6 h-6 mx-auto text-ink-500 mb-2" />
          <p className="text-ink-700 text-sm">{t("notifications.empty") || "No notifications yet"}</p>
        </Card>
      ) : (
        <Card className="divide-y divide-carbon-600">
          {list.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            const actions = ACTIONS_FOR[n.type] || [];
            const resolved = n.resolved;
            return (
              <div key={n.id} data-testid={`notif-${n.id}`}
                onClick={() => openNotificationRow(n)}
                className={`p-4 flex items-start gap-3 transition ${n.type === "proposal" ? "cursor-pointer hover:bg-carbon-800/40" : ""} ${n.unread ? "bg-electric-500/[0.05]" : ""}`}>
                <div className={`w-9 h-9 shrink-0 rounded-lg flex items-center justify-center ${
                  n.unread ? "bg-electric-500/10 text-electric-600 border border-electric-500/40" : "bg-carbon-750 border border-carbon-600 text-ink-500"
                }`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-ink-900 leading-snug">
                    {lang === "it" ? n.title : n.titleEn}
                  </div>
                  <div className="text-[11px] text-ink-500 mt-1 font-mono">{n.time}</div>

                  {actions.length > 0 && !resolved && (
                    <div className="flex gap-2 mt-3 flex-wrap" data-testid={`notif-actions-${n.id}`} onClick={(e) => e.stopPropagation()}>
                      {actions.includes("openProposal") && (
                        <button onClick={() => handleAction(n, "openProposal")} data-testid={`notif-open-proposal-${n.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-electric-500 hover:bg-electric-600 text-white text-xs font-medium transition">
                          <MessageSquare className="w-3.5 h-3.5" /> {t("requestDetail.openProposal")}
                        </button>
                      )}
                      {actions.includes("openRequest") && (
                        <button onClick={() => handleAction(n, "openRequest")} data-testid={`notif-open-request-${n.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-carbon-600 hover:border-electric-500 text-ink-800 hover:text-electric-600 text-xs font-medium transition">
                          <FileText className="w-3.5 h-3.5" /> {t("notifications.openRequest")}
                        </button>
                      )}
                      {actions.includes("message") && (
                        <button onClick={() => handleAction(n, "message")} data-testid={`notif-message-${n.id}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-carbon-600 hover:border-electric-500 text-ink-800 hover:text-electric-600 text-xs font-medium transition">
                          <MessageSquare className="w-3.5 h-3.5" /> {t("notifications.message")}
                        </button>
                      )}
                    </div>
                  )}

                  {resolved && (
                    <div className="mt-2.5 text-[11px] font-mono uppercase tracking-widest text-ink-500">
                      {resolved === "accept" && `✓ ${t("notifications.accepted")}`}
                      {resolved === "decline" && `✕ ${t("notifications.declined")}`}
                    </div>
                  )}
                </div>
                {n.unread && <span data-testid={`notif-unread-${n.id}`} className="w-2 h-2 rounded-full bg-electric-500 mt-2 shrink-0" />}
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
}
